import {
    type ApiResponse,
    BackendClient,
    type BackendClientParameters,
} from "@internal/backstage-plugin-utility-common";

import type { ReleaseStatusResult } from "../../types";

/**
 * Parameters for creating a {@link ReleaseStatusClient}.
 *
 * @public
 */
export type ReleaseStatusClientOptions = Omit<
    BackendClientParameters,
    "targetPluginId"
>;

/**
 * Per-request options for {@link ReleaseStatusClient} methods.
 *
 * @public
 */
export interface ReleaseStatusRequestOptions {
    /**
     * The entity's short name (metadata.name). Used for name-based matching of
     * Kubernetes workloads.
     */
    entityName: string;
    /**
     * The entity's annotations, used to resolve the GitHub repo and match
     * Kubernetes workloads (e.g. `github.com/project-slug`,
     * `backstage.io/kubernetes-id`).
     */
    entityAnnotations?: Record<string, string>;
}

/**
 * Client to fetch aggregated release-vs-deployment status from the Kubernetes
 * deployments backend module.
 *
 * The backend combines the production Kubernetes deployment with the entity's
 * latest GitHub release and returns a single {@link ReleaseStatusResult}.
 *
 * @example
 * ```typescript
 * const client = new ReleaseStatusClient({ discoveryApi, identityApi });
 * const result = await client.getReleaseStatusByEntity("component:default/my-service", {
 *   entityName: "my-service",
 *   entityAnnotations: entity.metadata.annotations,
 * });
 * ```
 *
 * @public
 */
export class ReleaseStatusClient extends BackendClient {
    constructor(options: ReleaseStatusClientOptions) {
        super({
            discoveryApi: options.discoveryApi,
            identityApi: options.identityApi,
            targetPluginId: "k8s",
        });
    }

    /**
     * Fetches the release-vs-deployment status for a catalog entity.
     *
     * This is a POST (not GET) despite being a read: the entity's full
     * annotations object is sent as a JSON body rather than a query string,
     * since richly-annotated entities can produce GET URLs long enough to be
     * rejected by infrastructure (ingress/WAF) before reaching the backend.
     *
     * @param entityRef - The stringified entity reference (e.g. "component:default/my-service")
     * @param options - The entity name and annotations used for matching
     * @returns ApiResponse with the aggregated {@link ReleaseStatusResult}
     */
    async getReleaseStatusByEntity(
        entityRef: string,
        options: ReleaseStatusRequestOptions
    ): Promise<ApiResponse<ReleaseStatusResult>> {
        return this.api<ReleaseStatusResult>(
            `ext/deployments/release-status/by-entity/${encodeURIComponent(
                entityRef
            )}`,
            {
                method: "POST",
                body: JSON.stringify({
                    entityName: options.entityName,
                    ...(options.entityAnnotations
                        ? { entityAnnotations: options.entityAnnotations }
                        : {}),
                }),
            }
        );
    }
}
